import moment from 'moment'
import { intervalsOverlapMoreThanOneMinute } from '@/helpers/actuals_overlap'

export const DRIVER_ACTUALS_SLICE_MINUTES = [15, 30, 45, 60]

export const DRIVER_ACTUALS_SLOTS_PER_DAY = 12

export const DRIVER_ACTUALS_SLOT_START_HOURS = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22]

function formatSlotHourLabel(hour) {
  if (hour === 0 || hour === 24)
    return '12am'
  if (hour === 12)
    return '12pm'
  if (hour < 12)
    return hour + 'am'
  return (hour - 12) + 'pm'
}

function buildSlotSlices(startHour) {
  const slices = []
  for (let hour = startHour; hour < startHour + 2; hour++) {
    for (const minutes of DRIVER_ACTUALS_SLICE_MINUTES)
      slices.push({ hour, minutes })
  }
  return slices
}

export const DRIVER_ACTUALS_TIMELINE_SLOTS = DRIVER_ACTUALS_SLOT_START_HOURS.map(startHour => ({
  startHour,
  label: formatSlotHourLabel(startHour),
  slices: buildSlotSlices(startHour),
}))

export const DRIVER_ACTUALS_TIMEFRAME_OPTIONS = [
  { value: 1, label: '1 Day' },
  { value: 3, label: '3 Day' },
  { value: 7, label: '7 Day' },
]

export function getTimelineDays(startDate, dayCount) {
  const days = []
  for (let i = 0; i < dayCount; i++)
    days.push(moment(startDate, 'MM-DD-YYYY').add(i, 'days').format('MM-DD-YYYY'))
  return days
}

export function getTimelineEndDate(startDate, dayCount) {
  return moment(startDate, 'MM-DD-YYYY').add(dayCount - 1, 'days').format('YYYY-MM-DD')
}

export function bookingOverlapsDateRange(start, end, startDate, dayCount) {
  if (!start || !end)
    return false
  const rangeStart = moment(startDate, 'MM-DD-YYYY').startOf('day')
  const rangeEnd = moment(startDate, 'MM-DD-YYYY').add(dayCount - 1, 'days').endOf('day')
  return moment(start).isBefore(rangeEnd) && moment(end).isAfter(rangeStart)
}

export function segmentFromBooking(booking, start, end, bookingIdField, timeKind) {
  const startM = moment(start)
  const endM = moment(end)
  return {
    driver: (booking.driver || '').trim(),
    booking,
    booking_id: booking[bookingIdField] || booking.bookingID,
    vehicle: booking.vehicle,
    start_datetime: start,
    end_datetime: end,
    timeline_date: startM.format('MM-DD-YYYY'),
    start_date: startM.format('MM-DD-YYYY'),
    finish_date: endM.format('MM-DD-YYYY'),
    start_hour: parseInt(startM.format('H'), 10),
    start_min: parseInt(startM.format('m'), 10),
    finish_hour: parseInt(endM.format('H'), 10),
    finish_min: parseInt(endM.format('m'), 10),
    driver_overlap: !!booking.driver_overlap,
    time_kind: timeKind || 'actual',
    booking_status: booking.status,
  }
}

export function segmentsOverlapByMinute(a, b) {
  return intervalsOverlapMoreThanOneMinute(
    a.start_datetime,
    a.end_datetime,
    b.start_datetime,
    b.end_datetime
  )
}

function clusterOverlappingSegments(segments) {
  const parent = new Map()
  const find = (seg) => {
    if (!parent.has(seg))
      parent.set(seg, seg)
    if (parent.get(seg) !== seg)
      parent.set(seg, find(parent.get(seg)))
    return parent.get(seg)
  }
  const union = (a, b) => {
    parent.set(find(a), find(b))
  }
  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      if (segmentsOverlapByMinute(segments[i], segments[j]))
        union(segments[i], segments[j])
    }
  }
  const clusters = new Map()
  for (const seg of segments) {
    const root = find(seg)
    if (!clusters.has(root))
      clusters.set(root, [])
    clusters.get(root).push(seg)
  }
  return Array.from(clusters.values())
}

function buildMergedRow(driver, segments) {
  const sorted = segments.slice().sort(
    (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)
  )
  const bookingIds = sorted.map(s => s.booking_id).filter(Boolean)
  const vehicles = [...new Set(sorted.map(s => s.vehicle).filter(Boolean))]
  const timelineDates = [...new Set(sorted.map(s => s.timeline_date))]
  let timelineDate = timelineDates[0] || ''
  if (timelineDates.length > 1)
    timelineDate = timelineDates[0] + ' – ' + timelineDates[timelineDates.length - 1]

  return {
    row_type: 'merged',
    driver,
    driver_label: driver,
    segments: sorted,
    segment_count: sorted.length,
    booking_id: bookingIds.length === 1 ? bookingIds[0] : bookingIds.join(', '),
    vehicle: vehicles.join(', '),
    timeline_date: timelineDate,
    start_datetime: sorted.reduce((min, s) => (
      !min || new Date(s.start_datetime) < new Date(min) ? s.start_datetime : min
    ), null),
    end_datetime: sorted.reduce((max, s) => (
      !max || new Date(s.end_datetime) > new Date(max) ? s.end_datetime : max
    ), null),
    driver_overlap: false,
  }
}

function buildSingleRow(segment, driverLabel) {
  return {
    row_type: 'single',
    ...segment,
    driver_label: driverLabel,
    has_time_overlap: true,
  }
}

export function buildDriverTimelineRows(bookings, getStartEnd, bookingIdField) {
  const segmentsByDriver = {}

  for (const booking of bookings) {
    if (!booking || booking.status === 'cancelled')
      continue
    const driver = (booking.driver || '').trim()
    if (!driver)
      continue
    const { start, end, timeKind } = getStartEnd(booking)
    if (!start || !end)
      continue
    if (new Date(end) <= new Date(start))
      continue

    if (!segmentsByDriver[driver])
      segmentsByDriver[driver] = []
    segmentsByDriver[driver].push(segmentFromBooking(booking, start, end, bookingIdField, timeKind))
  }

  const rows = []
  const sortedDrivers = Object.keys(segmentsByDriver).sort((a, b) => a.localeCompare(b))
  for (const driver of sortedDrivers) {
    const segments = segmentsByDriver[driver].sort(
      (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)
    )
    const clusters = clusterOverlappingSegments(segments)
    const mergeSegments = []
    const overlapRows = []

    for (const cluster of clusters) {
      if (cluster.length >= 2) {
        cluster.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
        cluster.forEach((segment) => {
          overlapRows.push(buildSingleRow(segment, ''))
        })
      } else {
        mergeSegments.push(cluster[0])
      }
    }

    mergeSegments.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))

    if (mergeSegments.length)
      rows.push(buildMergedRow(driver, mergeSegments))

    overlapRows.forEach((row, index) => {
      row.driver_label = !mergeSegments.length && index === 0 ? driver : ''
      rows.push(row)
    })
  }
  return rows
}

export function enrichDriverDisplayRows(rows) {
  const enriched = rows.map(row => ({
    ...row,
    skip_driver_cell: false,
    driver_rowspan: 1,
  }))
  let i = 0
  while (i < enriched.length) {
    const driverKey = enriched[i].driver
    if (!driverKey) {
      i++
      continue
    }
    let span = 1
    while (i + span < enriched.length && enriched[i + span].driver === driverKey)
      span++
    enriched[i].driver_rowspan = span
    for (let j = 1; j < span; j++)
      enriched[i + j].skip_driver_cell = true
    i += span
  }
  return enriched
}

function segmentIsErrorBooking(seg) {
  return seg.booking_status === 'error' || (seg.booking && seg.booking.status === 'error')
}

function statusBarClassForSegment(seg) {
  if (seg.time_kind === 'planned') {
    if (segmentIsErrorBooking(seg))
      return 'driver-actuals-bar--error'
    return 'driver-actuals-bar--planned'
  }
  return 'driver-actuals-bar--ok'
}

function overlapStripedBarClassForSegment(seg) {
  if (seg.time_kind === 'planned') {
    if (segmentIsErrorBooking(seg))
      return 'driver-actuals-bar--overlap-error'
    return 'driver-actuals-bar--overlap-planned'
  }
  return 'driver-actuals-bar--overlap-ok'
}

function shouldUseOverlapStripes(seg, row) {
  if (row && row.has_time_overlap)
    return true
  if (seg && seg.driver_overlap)
    return true
  if (row && row.driver_overlap)
    return true
  return false
}

export function utilizationBarClassForSegment(row, segment) {
  const seg = segment || row
  if (shouldUseOverlapStripes(seg, row))
    return overlapStripedBarClassForSegment(seg)
  return statusBarClassForSegment(seg)
}

export function getSegmentsAtCell(row, timelineDate, hour, minutes) {
  if (!row)
    return []
  const segments = row.row_type === 'merged' && row.segments ? row.segments : [row]
  return segments.filter(seg => (
    seg && isUtilSliceActive(timelineDate, hour, minutes, seg)
  ))
}

const UTIL_SLICE_ORDER = []
const UTIL_SLICE_ORDER_INDEX = new Map()
for (let hour = 0; hour < 24; hour++) {
  for (const minutes of DRIVER_ACTUALS_SLICE_MINUTES) {
    UTIL_SLICE_ORDER.push({ hour, minutes })
    UTIL_SLICE_ORDER_INDEX.set(`${hour}|${minutes}`, UTIL_SLICE_ORDER.length - 1)
  }
}

function utilSliceOrderIndex(hour, minutes) {
  const idx = UTIL_SLICE_ORDER_INDEX.get(`${hour}|${minutes}`)
  return idx === undefined ? -1 : idx
}

function isFirstUtilSliceForSegment(timelineDate, hour, minutes, segment) {
  if (!isUtilSliceActive(timelineDate, hour, minutes, segment))
    return false
  const idx = utilSliceOrderIndex(hour, minutes)
  if (idx < 0)
    return false
  for (let i = 0; i < idx; i++) {
    const slice = UTIL_SLICE_ORDER[i]
    if (isUtilSliceActive(timelineDate, slice.hour, slice.minutes, segment))
      return false
  }
  return true
}

function segmentsAreBackToBack(prevSeg, nextSeg) {
  const prevEnd = prevSeg && prevSeg.end_datetime
  const nextStart = nextSeg && nextSeg.start_datetime
  if (!prevEnd || !nextStart)
    return false
  return moment(prevEnd).valueOf() === moment(nextStart).valueOf()
}

function hasSegmentBoundaryAtCell(row, timelineDate, hour, minutes) {
  if (row.row_type !== 'merged' || !row.segments || row.segments.length < 2)
    return false
  const segments = row.segments.slice().sort(
    (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime)
  )
  for (let i = 1; i < segments.length; i++) {
    if (!segmentsAreBackToBack(segments[i - 1], segments[i]))
      continue
    if (isFirstUtilSliceForSegment(timelineDate, hour, minutes, segments[i]))
      return true
  }
  return false
}

function getActiveSegmentForCell(row, timelineDate, hour, minutes) {
  if (row.row_type === 'merged' && row.segments) {
    for (const segment of row.segments) {
      if (isUtilSliceActive(timelineDate, hour, minutes, segment))
        return segment
    }
    return null
  }
  if (isUtilSliceActive(timelineDate, hour, minutes, row))
    return row
  return null
}

function getBarShapeClass(segment, timelineDate, hour, minutes) {
  const idx = utilSliceOrderIndex(hour, minutes)
  const prev = idx > 0 ? UTIL_SLICE_ORDER[idx - 1] : null
  const next = idx < UTIL_SLICE_ORDER.length - 1 ? UTIL_SLICE_ORDER[idx + 1] : null
  const hasPrev = prev && isUtilSliceActive(timelineDate, prev.hour, prev.minutes, segment)
  const hasNext = next && isUtilSliceActive(timelineDate, next.hour, next.minutes, segment)
  if (!hasPrev && !hasNext)
    return 'driver-actuals-bar--solo'
  if (!hasPrev)
    return 'driver-actuals-bar--start'
  if (!hasNext)
    return 'driver-actuals-bar--end'
  return 'driver-actuals-bar--mid'
}

export function getCellBarClasses(row, timelineDate, hour, minutes) {
  const segment = getActiveSegmentForCell(row, timelineDate, hour, minutes)
  if (!segment)
    return []
  const classes = [
    utilizationBarClassForSegment(row, segment),
    getBarShapeClass(segment, timelineDate, hour, minutes),
  ]
  if (hasSegmentBoundaryAtCell(row, timelineDate, hour, minutes))
    classes.push('driver-actuals-bar--gap')
  return classes
}

export function isUtilSliceActive(fromDate, hour, minutes, segment) {
  if (!segment)
    return false

  const fromDateObj = moment(fromDate, 'MM-DD-YYYY')
  const startDate = moment(segment.start_date, 'MM-DD-YYYY')
  const finishDate = moment(segment.finish_date, 'MM-DD-YYYY')
  const startHour = segment.start_hour
  const startMin = segment.start_min
  const finishHour = segment.finish_hour
  const finishMin = segment.finish_min

  if (startDate.isBefore(fromDateObj, 'day') && fromDateObj.isBefore(finishDate, 'day'))
    return true
  if (startDate.isBefore(fromDateObj, 'day') && !finishDate.isBefore(fromDateObj, 'day')) {
    if (hour < finishHour || (hour === finishHour && finishMin >= minutes))
      return true
  } else if (finishDate.isAfter(fromDateObj, 'day') && !startDate.isAfter(fromDateObj, 'day')) {
    if (startHour < hour || (hour === startHour && startMin <= minutes))
      return true
  } else if (!startDate.isAfter(fromDateObj, 'day') && !finishDate.isBefore(fromDateObj, 'day')) {
    if (startHour < hour && hour < finishHour)
      return true
    if (startHour <= hour && hour <= finishHour) {
      if (startHour === hour && startMin < minutes)
        return true
      if (finishHour === hour && finishMin >= minutes)
        return true
    }
  }
  return false
}
